import type { FilterRuleModel } from '@/shared/generated'
import type { ErrorSchema } from '@/shared/tools/json-schema-utils'

import { prop } from 'lodash/fp'
import { PlusIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { formatFilterRule } from '@/entities/column'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { getLocalization } from '@/shared/components/filter/localization'
import {
	getFiledConfig,
	QueryBuilder,
	QueryBuilderAddButton,
	QueryBuilderEmpty,
	QueryBuilderRulesList,
} from '@/shared/components/query-builder'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Label } from '@/shared/components/ui/label'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { useAppStore } from '@/shared/contexts/app'

import { useFiltersParams } from '../lib/useFiltersParams'

type AssignmentSet = {
	name: string
	filters: FilterRuleModel[]
}
type AutoAssignmentPanelProps = {
	onChange(data: AssignmentSet): void
	formData: AssignmentSet
	error?: ErrorSchema
}
export const AssignmentFields = ({
	formData,
	onChange,
	error,
}: AutoAssignmentPanelProps) => {
	const { t } = useTranslation()
	const dateFormat = useAppStore((state) => state.dateFormat)
	const filters = formData?.filters || []
	const { fields, dataChoices, isLoading } = useFiltersParams()
	const fieldConfig = getFiledConfig(fields, dataChoices)

	return isLoading ? (
		<div className='flex flex-col space-y-2'>
			<Skeleton className='h-10 w-full' />
			<Skeleton className='h-10 w-full' />
		</div>
	) : (
		<ErrorBoundary
			fallback={() => (
				<Alert variant='destructive'>
					<AlertDescription>
						🐞{' '}
						{t('ui_fragment_error', {
							defaultValue:
								'Oops ... it looks like an unexpected error has occurred. Our team`s been notified.',
						})}
					</AlertDescription>
				</Alert>
			)}
		>
			<div className='grid gap-1.5'>
				<Label className='text-sm'>
					{t('app.filters', {
						defaultValue: 'Filters',
					})}
				</Label>
				<QueryBuilder
					dateFormat={dateFormat}
					enableReferencing
					rules={filters || []}
					fieldConfig={fieldConfig}
					locale={getLocalization(t)}
					errorSchema={error?.filters}
					onRulesChange={(filters) =>
						onChange({
							name: formData?.name,
							filters: filters.map(formatFilterRule),
						})
					}
				>
					<QueryBuilderEmpty className='border-b' />
					<QueryBuilderRulesList className='max-h-100 overflow-y-auto border-y py-2' />
					{prop('filters', error) && (
						<p className='text-sm text-destructive'>
							{prop('filters', error)?.message ||
								t('validation.invalid_rules', {
									defaultValue:
										'Your filters are invalid, please check them again and try again.',
								})}
						</p>
					)}
					<QueryBuilderAddButton
						data-testid='filters-popover-add'
						className='mr-auto'
					>
						<PlusIcon />
						{t('filters_add_filter', {
							defaultValue: 'Add filter',
						})}
					</QueryBuilderAddButton>
				</QueryBuilder>
			</div>
		</ErrorBoundary>
	)
}
